import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { preloadReferences } from '../services/api';

import { PLACEHOLDER_QUESTIONS, NAV_MAP } from './lumera-companion/constants';
import { recordSlideconFunnelEvent } from '../lib/slideconFunnel';
import useLumeraVoice from './lumera-companion/useLumeraVoice';
import CompanionHeader from './lumera-companion/CompanionHeader';
import ConversationHistorySidebar from './lumera-companion/ConversationHistorySidebar';
import EmptyState from './lumera-companion/EmptyState';
import ResponseView from './lumera-companion/ResponseView';
import QuestionInput from './lumera-companion/QuestionInput';
import { buildSermonFromConversation, downloadCompanionPdf } from './lumera-companion/companionConversationUtils';
import { streamCompanionAnswer } from './lumera-companion/streamCompanionAnswer';
import StreamingTurn from './lumera-companion/StreamingTurn';
import { LumeraPlusBadge } from './lumera-companion/PremiumLock';
import usePremiumGate from '../hooks/usePremiumGate';
import { Mic } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lumera AI Companion — Scripture-based guidance.
 *
 * Owns state, API calls and voice-command routing. UI is composed from the
 * dedicated subcomponents in `./lumera-companion/`.
 */
const LumeraCompanion = () => {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [question, setQuestion] = useState('');
  const [lastQuestion, setLastQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState(null);
  const [turns, setTurns] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [limits, setLimits] = useState(null);
  const [buildingSermonNudge, setBuildingSermonNudge] = useState(false);
  const [streaming, setStreaming] = useState(null);
  const messagesEndRef = useRef(null);
  const gate = usePremiumGate();

  const voice = useLumeraVoice({
    language,
    onTranscript: (transcript) => {
      setQuestion(transcript);
      handleVoiceCommand(transcript);
    },
  });

  /* ---------------- Rotating placeholder ---------------- */
  useEffect(() => {
    const questions = PLACEHOLDER_QUESTIONS[language] || PLACEHOLDER_QUESTIONS.en;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % questions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [language]);

  /* ---------------- Initial load ---------------- */
  useEffect(() => {
    loadConversations();
    loadLimits();
    maybeShowSlideconUnifiedTierToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After an in-place trial start/upgrade, refresh quota and un-redact the open thread.
  const prevPremiumRef = useRef(gate.isPremium);
  useEffect(() => {
    if (gate.isPremium && !prevPremiumRef.current) {
      loadLimits();
      if (conversationId) handleLoadConversation(conversationId);
    }
    prevPremiumRef.current = gate.isPremium;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.isPremium]);

  /* ---------------- One-time "Slidecon is now included" toast ----------------
   * Fires once for users on any paid plan to announce the v58 unified-tier
   * change. localStorage flag prevents repeats. Falls back silently if any
   * piece of the chain is missing. */
  const maybeShowSlideconUnifiedTierToast = async () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      if (localStorage.getItem('lumera-slidecon-included-seen') === '1') return;
      const r = await axios.get(`${BACKEND_URL}/api/payments/subscription`, {
        withCredentials: true,
      });
      const plan = r.data?.plan;
      if (!plan?.includes_slidecon) return;  // free / no sub → skip
      localStorage.setItem('lumera-slidecon-included-seen', '1');
      recordSlideconFunnelEvent('v70_toast_seen');
      toast.success(
        language === 'es'
          ? 'Slidecon Pro ahora viene incluido en tu plan'
          : 'Slidecon Pro is now included with your plan',
        {
          description:
            language === 'es'
              ? 'Toca el botón "Slideshow" en cualquier respuesta para crear diapositivas profesionales.'
              : 'Tap the "Slideshow" button on any answer to turn it into pro slides.',
          duration: 10000,
          id: 'slidecon-included',
        },
      );
    } catch {
      /* silent — toast is non-critical */
    }
  };

  /* ---------------- Follow-up chips → re-ask ----------------
   * The resourceful Companion emits `lumera:companion-ask` when the user
   * taps a follow-up chip. We catch it here, fill the input, and submit. */
  // Ref always points at the latest handleAskQuestion so the once-registered
  // listener below never uses a stale closure (stale conversationId = null
  // would start a NEW backend conversation and lose LLM context).
  const askRef = useRef(null);
  useEffect(() => {
    const onAsk = (e) => {
      const q = e?.detail?.question;
      if (!q || typeof q !== 'string') return;
      setQuestion(q);
      setTimeout(() => askRef.current?.(q), 50);
    };
    window.addEventListener('lumera:companion-ask', onAsk);
    return () => window.removeEventListener('lumera:companion-ask', onAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Deep-link `?conversation=<id>` ---------------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const convId = params.get('conversation');
    if (convId) {
      handleLoadConversation(convId);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lumera/conversations`, {
        withCredentials: true,
      });
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  };

  const loadLimits = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lumera/limits`, {
        withCredentials: true,
      });
      setLimits(response.data);
    } catch (err) {
      console.error('Error loading limits:', err);
    }
  };

  /* ---------------- Voice command routing ---------------- */
  const handleVoiceCommand = async (transcript) => {
    const t = transcript.toLowerCase();
    // Reminder commands
    if (t.includes('remind me') || t.includes('reminder') || t.includes('recuérdame')) {
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/reminders/voice-command`,
          { command: transcript },
          { withCredentials: true },
        );
        if (response.data.success) {
          toast.success(response.data.message || (language === 'es' ? 'Recordatorio creado' : 'Reminder created'));
          voice.speak(response.data.message);
        }
      } catch (err) {
        console.error('Error creating reminder:', err);
        toast.error(language === 'es' ? 'Error al crear recordatorio' : 'Error creating reminder');
      }
      return true;
    }
    // Navigation commands
    if (['open', 'show', 'go to', 'abrir', 'mostrar', 'ir a'].some((w) => t.includes(w))) {
      for (const [keyword, route] of Object.entries(NAV_MAP)) {
        if (t.includes(keyword)) {
          navigate(route);
          toast.success(language === 'es' ? `Abriendo ${keyword}` : `Opening ${keyword}`);
          return true;
        }
      }
      toast.info(language === 'es' ? 'No entendí el comando de navegación' : 'Could not understand navigation command');
      return true;
    }
    // Save/Bookmark
    if (['save', 'bookmark', 'guardar', 'marcar'].some((w) => t.includes(w))) {
      if (currentResponse?.scripture?.primary_reference) {
        const verseRef = currentResponse.scripture.primary_reference;
        toast.success(language === 'es' ? `${verseRef} guardado` : `${verseRef} saved`);
        voice.speak(language === 'es' ? 'Versículo guardado' : 'Verse saved');
      } else {
        toast.error(language === 'es' ? 'No hay versículo para guardar' : 'No verse to save');
      }
      return true;
    }
    // Sermon prep
    if (['prepare', 'preparar'].some((w) => t.includes(w)) && ['sermon', 'sermón'].some((w) => t.includes(w))) {
      navigate('/sermon-prep');
      toast.success(language === 'es' ? 'Abriendo preparación de sermones' : 'Opening sermon preparation');
      return true;
    }
    return false;
  };

  const speakResponse = () => {
    if (!currentResponse) return;
    let text = '';
    (currentResponse.scripture?.verses || []).forEach((v) => {
      text += `${v.reference}. ${v.text}. `;
    });
    if (currentResponse.understanding) text += `What this means: ${currentResponse.understanding}. `;
    if (currentResponse.application?.length) {
      text += 'How to apply this today: ';
      currentResponse.application.forEach((step, i) => {
        text += `${i + 1}. ${step}. `;
      });
    }
    voice.speak(text);
  };

  /* ---------------- Ask (streamed) ---------------- */
  const handleAskQuestion = async (overrideText) => {
    const q = (overrideText || question).trim();
    if (!q || isLoading) return;
    setIsLoading(true);
    setQuestion('');
    setStreaming({ question: q, text: '' });
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const data = await streamCompanionAnswer({
        question: q,
        conversationId,
        language,
        onDelta: (chunk) => setStreaming((prev) => (prev ? { ...prev, text: prev.text + chunk } : prev)),
      });
      if (data.success) {
        setCurrentResponse(data.response);
        setTurns((prev) => [...prev, { question: q, response: data.response }]);
        setConversationId(data.conversation_id);
        setLastQuestion(q);

        // Context-aware preload
        try {
          const scr = data.response?.scripture || {};
          const refs = [];
          if (scr.primary_reference) refs.push(scr.primary_reference);
          (scr.verses || []).forEach((v) => v?.reference && refs.push(v.reference));
          (scr.related_passages || []).forEach((r) => r && refs.push(r));
          if (refs.length) preloadReferences(refs, scr.translation || 'KJV');
        } catch (_) {/* best-effort */}

        setLimits((prev) => ({ ...prev, remaining_questions: data.remaining_questions }));
        loadConversations();
      }
    } catch (err) {
      if (err?.limit_reached) {
        setLimits((prev) => ({ ...prev, remaining_questions: 0 }));
        toast.error(
          language === 'es'
            ? 'Llegaste a tu límite diario. Actualiza a Lumera+ para preguntas ilimitadas.'
            : err.upgrade_prompt || 'Daily question limit reached',
        );
      } else {
        console.error('Error asking question:', err);
        toast.error(language === 'es' ? 'Error al procesar pregunta' : 'Error processing question');
      }
      setQuestion(q);
    } finally {
      setStreaming(null);
      setIsLoading(false);
    }
  };

  askRef.current = handleAskQuestion;

  /* ---------------- Conversation management ---------------- */
  const handleLoadConversation = async (convId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lumera/conversation/${convId}`, {
        withCredentials: true,
      });
      if (response.data.success) {
        const conv = response.data.conversation;
        setConversationId(conv.conversation_id);
        // Rebuild the full thread from the persisted messages so the user
        // sees every past question + answer, not just the last one.
        const rebuilt = [];
        let pendingQuestion = '';
        (conv.messages || []).forEach((m) => {
          if (m.role === 'user' && typeof m.content === 'string') {
            pendingQuestion = m.content;
          } else if (m.role === 'assistant' && m.content && typeof m.content === 'object') {
            rebuilt.push({ question: pendingQuestion, response: m.content });
            pendingQuestion = '';
          }
        });
        setTurns(rebuilt);
        const last = rebuilt[rebuilt.length - 1];
        if (last) {
          setCurrentResponse(last.response);
          setLastQuestion(last.question);
        }
        setShowHistory(false);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      toast.error(language === 'es' ? 'Error al cargar conversación' : 'Error loading conversation');
    }
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/lumera/conversation/${convId}`, { withCredentials: true });
      loadConversations();
      if (conversationId === convId) {
        setConversationId(null);
        setCurrentResponse(null);
        setTurns([]);
      }
      toast.success(language === 'es' ? 'Conversación eliminada' : 'Conversation deleted');
    } catch (err) {
      console.error('Error deleting conversation:', err);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handleNewConversation = () => {
    setConversationId(null);
    setCurrentResponse(null);
    setTurns([]);
    setQuestion('');
  };

  const handleBuildSermonNudge = async () => {
    if (!conversationId || buildingSermonNudge) return;
    if (!isPremium) { gate.requirePremium(language === 'es' ? 'Crear sermón' : 'Make it a Sermon'); return; }
    setBuildingSermonNudge(true);
    const draftId = await buildSermonFromConversation(conversationId);
    setBuildingSermonNudge(false);
    if (draftId) {
      navigate(`/sermon-prep?draft_id=${encodeURIComponent(draftId)}`);
    } else {
      toast.error(language === 'es' ? 'No se pudo crear el sermón' : 'Could not build the sermon');
    }
  };

  const currentPlaceholder = (PLACEHOLDER_QUESTIONS[language] || PLACEHOLDER_QUESTIONS.en)[placeholderIndex];
  const isPremium = limits ? limits.subscription_tier === 'premium' : gate.isPremium;
  const requirePremium = (label) => gate.requirePremium(label);
  const handleSidebarPdf = (convId) => {
    if (!isPremium) { requirePremium(language === 'es' ? 'Exportar PDF' : 'PDF export'); return; }
    downloadCompanionPdf(convId);
  };
  const isLimitReached = limits?.remaining_questions === 0 && !isPremium;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-900 dark:to-slate-800">
      <CompanionHeader
        language={language}
        limits={limits}
        isPremium={isPremium}
        isLimitReached={isLimitReached}
        onToggleHistory={() => setShowHistory((v) => !v)}
        onNewConversation={handleNewConversation}
      />

      <div className="flex-1 overflow-hidden flex">
        {showHistory && (
          <ConversationHistorySidebar
            language={language}
            conversations={conversations}
            onLoad={handleLoadConversation}
            onDelete={handleDeleteConversation}
            onDownloadPdf={handleSidebarPdf}
            isPremium={isPremium}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {turns.length === 0 && !streaming ? (
              <EmptyState
                language={language}
                isLoading={isLoading}
                onAsk={(prompt) => {
                  setQuestion(prompt);
                  setTimeout(() => handleAskQuestion(prompt), 60);
                }}
                limits={limits}
                isPremium={isPremium}
              />
            ) : (
              <div className="space-y-8">
                {turns.map((turn, idx) => {
                  const isLast = idx === turns.length - 1;
                  return (
                    <ResponseView
                      key={idx}
                      language={language}
                      currentResponse={turn.response}
                      questionText={turn.question}
                      conversationId={conversationId}
                      isPremium={isPremium}
                      isSpeaking={voice.isSpeaking}
                      onSpeak={speakResponse}
                      onStopSpeak={voice.stopSpeaking}
                      expandedSections={expandedSections[idx] || {}}
                      onToggleSection={(section) =>
                        setExpandedSections((prev) => ({
                          ...prev,
                          [idx]: { ...(prev[idx] || {}), [section]: !(prev[idx]?.[section]) },
                        }))
                      }
                      lastQuestion={turn.question}
                      showToolbar={isLast && !streaming}
                      messagesEndRef={isLast && !streaming ? messagesEndRef : undefined}
                      onRequirePremium={requirePremium}
                    />
                  );
                })}
                {turns.length >= 3 && conversationId && !streaming && (
                  <div
                    className="max-w-3xl mx-auto rounded-2xl border border-amber-300 dark:border-amber-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 p-4 flex items-center justify-between gap-4 shadow-sm"
                    data-testid="companion-sermon-nudge"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-600/15 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                        <Mic className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                          {language === 'es'
                            ? 'Esta conversación da para un sermón'
                            : 'This conversation is rich enough for a sermon'}
                        </p>
                        <p className="text-xs text-amber-800/80 dark:text-amber-200/70">
                          {language === 'es'
                            ? 'Conviértela en un bosquejo de sermón con un toque.'
                            : 'Turn it into a structured sermon outline in one tap.'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleBuildSermonNudge}
                      disabled={buildingSermonNudge}
                      data-testid="companion-sermon-nudge-btn"
                      className="shrink-0 px-4 py-2 rounded-full bg-gradient-to-r from-amber-600 to-orange-700 text-white text-sm font-semibold hover:from-amber-700 hover:to-orange-800 disabled:opacity-60 transition-colors"
                    >
                      {buildingSermonNudge
                        ? (language === 'es' ? 'Creando…' : 'Building…')
                        : (language === 'es' ? 'Crear sermón' : 'Make it a Sermon')}
                      {!isPremium && <LumeraPlusBadge className="ml-2" />}
                    </button>
                  </div>
                )}
                {streaming && (
                  <StreamingTurn
                    language={language}
                    question={streaming.question}
                    text={streaming.text}
                    messagesEndRef={messagesEndRef}
                  />
                )}
              </div>
            )}
          </div>

          <QuestionInput
            language={language}
            question={question}
            onChangeQuestion={setQuestion}
            onAsk={handleAskQuestion}
            isLoading={isLoading}
            isLimitReached={isLimitReached}
            isListening={voice.isListening}
            toggleListening={voice.toggleListening}
            micPermission={voice.micPermission}
            isMobile={voice.isMobile}
            placeholder={currentPlaceholder}
          />
        </div>
      </div>
      {gate.dialog}
    </div>
  );
};

export default LumeraCompanion;
